import TKSearch from '@tk/controls/tk.search';
import { fetchRequest } from '@tk/utilities/tk.fetch';
import { isOutsideShapes } from '@tk/utilities/tk.shape.detector';

interface SearchResponse {
    html: string;
}

export enum MessageType {
    tooShort = 1,
    dangerousString = 2,
    disallowedCharacters = 3,
}

export default class DIWSearch extends TKSearch {
    static MessageType = MessageType;
    defaultSearchUrl: string;
    mobileBtn: HTMLButtonElement | null;

    constructor() {
        super();

        this.defaultSearchUrl = this.getAttribute('data-tk-default-search-url') || '';
        this.mobileBtn = document.querySelector('[data-tk-search-mobile-btn]');
    }

    connectedCallback(): void {
        super.connectedCallback();

        if (this.mobileBtn) {
            this.registerMobileListener();
        }
    }

    override getSuggest(): void {
        if (!this.defaultSearchUrl) throw new Error('Missing default search url');

        if (!this.directSearchResult) return;

        this.directSearchInputObj = this.validateSearchInput();
        this.modifyDOM();

        if (!this.directSearchInputObj.valid) {
            this.classList.add(this.activeResultClassName);
            if (this.directSearchInputObj.hasDangerousString) {
                this.setErrorMessage(MessageType.dangerousString);
            } else if (this.directSearchInputObj.hasDisallowedChars) {
                this.setErrorMessage(MessageType.disallowedCharacters);
            } else {
                this.setErrorMessage(MessageType.tooShort);
            }
            return;
        }

        this.resetResultList();

        this.timerID && clearTimeout(this.timerID);
        this.timerID = window.setTimeout(() => {
            // append Progress-Bar
            this.setProgressBar();

            const searchData = this.setDataForRequest();
            const data: Record<string, string> = Object.fromEntries(Object.entries(searchData) as [string, string][]);

            let searchTerm = data?.inputValue;

            const minLen = Number(this.attributeSearchMinLength) || 3;

            searchTerm = (searchTerm ?? '')
                .split('%20')
                .filter((part) => part.length >= minLen)
                .join('%20');

            const url = `${this.defaultSearchUrl}?query=${searchTerm}`;

            const getSuggestAction = this.onDirectSearchSuggest.bind(this);

            fetchRequest({
                requestURL: url,
                resolveHandler: getSuggestAction,
                payload: data,
            });
        }, 500);
    }

    onDirectSearchSuggest(response: TKResponse<SearchResponse>) {
        if (!this.directSearchResult) {
            return;
        }

        this.classList.add(this.activeResultClassName);
        this.directSearchResult.innerHTML = response.dataAsHtml;
    }

    registerMobileListener(): void {
        const searchInput = this.directSearchForm?.querySelector('input');
        if (!searchInput) return;
        const action = () => {
            searchInput.focus();
            this.setActiveClass();
        };
        this.pushListener({ event: 'click', element: this.mobileBtn!, action });
    }

    focusOut(event: PointerEvent): void {
        if (event.pointerId === -1) return;

        const fieldElement = this.directSearchInputElement;
        const resultElement = this.directSearchResult;
        if (!fieldElement || !resultElement) return;
        const fieldElementRect = fieldElement.getBoundingClientRect();
        const resultRect = resultElement.getBoundingClientRect();
        const rectangles = [{ rectangle: fieldElementRect }, { rectangle: resultRect }];

        if (isOutsideShapes(event, rectangles) && event.target != this.mobileBtn) {
            this.classList.remove(this.activeClassName);
            this.resetSearch();
        }
    }
}
